import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DEAPActivite6Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const audioRef = useRef(null);

  const slides = [
    // Slide 1 - Titre
    {
      type: 'title',
      title: "Activité 6",
      subtitle: "Accueil, information et communication auprès des personnes et de leur entourage",
      gradient: "linear-gradient(135deg, #EC4899 0%, #DB2777 50%, #BE185D 100%)"
    },
    // Slide 2 - Sommaire interactif
    {
      type: 'sommaire',
      title: "Sommaire",
      sections: [
        { num: "1", title: "Fondements de la communication", items: ["Communication professionnelle", "Verbal et non verbal"], color: "#EC4899", slideTarget: 3 },
        { num: "2", title: "Accueil matin/soir", items: ["Accueil du matin", "Transmission du soir", "Familiarisation"], color: "#8B5CF6", slideTarget: 7 },
        { num: "3", title: "Communication adaptée", items: ["Avec l'enfant", "Communication gestuelle (signes)", "Handicap", "Situation d'urgence"], color: "#3B82F6", slideTarget: 13 },
        { num: "4", title: "Posture professionnelle", items: ["Non verbal", "Analyse de pratique", "Messages clés"], color: "#10B981", slideTarget: 26 }
      ]
    },
    // Slide 3 - Section Communication
    {
      type: 'section',
      title: "FONDEMENTS DE LA COMMUNICATION",
      subtitle: "Un acte professionnel à part entière",
      gradient: "linear-gradient(135deg, #EC4899 0%, #DB2777 100%)"
    },
    // Slide 4 - Communication professionnelle
    {
      type: 'content',
      title: "La communication professionnelle",
      definition: "La communication est un acte professionnel à part entière. Elle ne se limite pas à transmettre une information.",
      content: "Elle participe à la qualité d'accueil, à la sécurité affective de l'enfant et au partenariat avec les familles. L'auxiliaire de puériculture communique en permanence : par la parole, par le regard, par le silence, par l'attitude corporelle.",
      extraInfo: [
        { term: "Repose sur", desc: "L'écoute active, la reformulation, l'objectivité et la discrétion" },
        { term: "S'inscrit dans", desc: "Le respect du secret professionnel et du projet pédagogique" }
      ]
    },
    // Slide 5 - Verbal et non verbal
    {
      type: 'content',
      title: "Cohérence verbal / non verbal",
      definition: "La cohérence entre le verbal et le non verbal est essentielle.",
      content: "Une parole rassurante accompagnée d'une posture fermée crée un message contradictoire. À l'inverse, une posture ouverte, un sourire sincère et une voix posée renforcent la confiance.",
      warning: "L'enfant perçoit l'état interne de l'adulte. Une posture stable contribue à contenir l'angoisse."
    },
    // Slide 6 - Compétences visées
    {
      type: 'list',
      title: "Compétences relationnelles visées",
      items: [
        { term: "Accueillir", desc: "L'enfant, sa famille et l'équipe" },
        { term: "Informer", desc: "De manière claire et professionnelle" },
        { term: "Communiquer", desc: "Dans le respect du secret professionnel" },
        { term: "S'adapter", desc: "Au cadre institutionnel et aux protocoles" },
        { term: "Gérer ses émotions", desc: "Et adopter une posture réflexive" }
      ],
      alertColor: "#EC4899"
    },
    // Slide 7 - Section Accueil
    {
      type: 'section',
      title: "ACCUEIL MATIN ET SOIR",
      subtitle: "Moments de transition essentiels",
      gradient: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)"
    },
    // Slide 8 - Accueil du matin
    {
      type: 'content',
      title: "L'accueil du matin",
      definition: "L'accueil du matin constitue un moment de transition délicat entre la maison et la collectivité.",
      content: "L'auxiliaire de puériculture adopte une posture disponible et rassurante. Elle se met à hauteur de l'enfant, l'appelle par son prénom et accueille les informations données par le parent.",
      extraInfo: [
        { term: "Objectif", desc: "Sécuriser la séparation et recueillir les informations utiles" },
        { term: "Informations à recueillir", desc: "Sommeil, alimentation, état émotionnel de l'enfant" },
        { term: "Bénéfice", desc: "Individualisation de l'accompagnement" }
      ]
    },
    // Slide 9 - Transmission du soir
    {
      type: 'content',
      title: "L'accueil du soir",
      definition: "L'accueil du soir permet de restituer les éléments importants de la journée.",
      content: "La transmission est factuelle, valorisante et professionnelle. Elle rassure les parents et renforce la continuité éducative.",
      extraInfo: [
        { term: "Transmission", desc: "Factuelle, valorisante et professionnelle" },
        { term: "Objectif", desc: "Rassurer les parents et renforcer la continuité éducative" }
      ],
      warning: "La transmission du soir ne doit pas être bâclée même si les parents sont pressés."
    },
    // Slide 10 - Familiarisation
    {
      type: 'contentLargeImage',
      title: "La période de familiarisation",
      definition: "La période de familiarisation permet une séparation progressive et sécurisante.",
      content: "L'auxiliaire de puériculture accompagne l'enfant et ses parents dans cette étape. Elle observe les réactions de l'enfant, verbalise les émotions et soutient les parents parfois inquiets.",
      extraInfo: [
        { term: "Rôle de l'AP", desc: "Accompagner progressivement la séparation" },
        { term: "Actions", desc: "Observer, verbaliser les émotions, soutenir les parents" },
        { term: "Recueil", desc: "Habitudes de vie pour assurer la continuité famille/structure" }
      ],
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/8mv4eu1v_image.png"
    },
    // Slide 11 - Rencontres familles
    {
      type: 'list',
      title: "Les temps de rencontre avec les familles",
      items: [
        { term: "Cafés parents", desc: "Favorisent le partenariat éducatif" },
        { term: "Fêtes", desc: "Renforcent la confiance et l'image positive de la structure" },
        { term: "Sorties", desc: "Moments de partage et d'échange" },
        { term: "Posture AP", desc: "Écoute, valorisation des compétences parentales, cadre professionnel" }
      ],
      alertColor: "#8B5CF6"
    },
    // Slide 12 - Secret professionnel
    {
      type: 'content',
      title: "Secret professionnel et discrétion",
      definition: "La communication professionnelle s'inscrit dans le respect du secret professionnel.",
      warning: "Il est interdit de transmettre des informations sensibles devant d'autres familles, même pour gagner du temps.",
      extraInfo: [
        { term: "Règle", desc: "Ne jamais divulguer d'informations confidentielles" },
        { term: "Transmission écrite", desc: "Assure la traçabilité et la sécurité professionnelle" }
      ]
    },
    // Slide 13 - Section Communication adaptée
    {
      type: 'section',
      title: "COMMUNICATION ADAPTÉE",
      subtitle: "Enfant, handicap et urgence",
      gradient: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)"
    },
    // Slide 14 - Communication enfant
    {
      type: 'content',
      title: "La communication avec l'enfant",
      definition: "Communiquer avec l'enfant favorise son développement affectif, social et langagier.",
      content: "L'auxiliaire de puériculture verbalise les actions, explique les soins et nomme les émotions. La posture à hauteur d'enfant, le regard bienveillant et le ton calme renforcent la sécurité affective.",
      extraInfo: [
        { term: "Verbaliser les actions", desc: "Structure le langage et sécurise l'enfant" },
        { term: "Posture", desc: "À hauteur d'enfant, regard bienveillant, ton calme" },
        { term: "Bénéfices", desc: "Soutient l'autonomie et l'estime de soi" }
      ]
    },
    // Slide 15 - Pictogrammes
    {
      type: 'contentLargeImage',
      title: "Supports visuels et pictogrammes",
      definition: "L'utilisation de pictogrammes en crèche permet de soutenir la compréhension.",
      content: "Les supports visuels ne remplacent pas totalement la parole mais la renforcent. Ils sont particulièrement utiles pour les enfants en situation de handicap ou avec des difficultés de communication.",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/6gs0wudh_image.png"
    },
    // Slide 16 - Communication gestuelle intro
    {
      type: 'content',
      title: "La communication gestuelle associée à la parole",
      definition: "Utilisation de gestes simples issus de la LSF associés systématiquement au mot prononcé.",
      content: "Cette pratique permet au jeune enfant, dont le langage oral n'est pas encore développé, d'exprimer ses besoins, ses émotions et ses envies. Avant même de pouvoir parler, l'enfant est capable de reproduire des gestes simples.",
      extraInfo: [
        { term: "Objectif", desc: "Soutenir la parole, pas la remplacer" },
        { term: "Principe", desc: "Le professionnel prononce toujours le mot en même temps qu'il réalise le geste" },
        { term: "Bénéfice", desc: "Offre un moyen d'expression supplémentaire avant l'acquisition du langage oral" }
      ]
    },
    // Slide 17 - Infographie signes bébé
    {
      type: 'infographic',
      title: "Mes premiers signes avec bébé",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/brxfyk4x_image.png"
    },
    // Slide 18 - Prévention frustration
    {
      type: 'content',
      title: "Un outil de prévention de la frustration",
      definition: "Chez le jeune enfant, l'incapacité à se faire comprendre peut générer frustration, pleurs ou agitation.",
      content: "Les gestes simples comme manger, encore, dodo, eau, fini, changer permettent à l'enfant d'exprimer un besoin immédiat. Lorsque l'enfant parvient à se faire comprendre, il se sent compétent et reconnu.",
      extraInfo: [
        { term: "Résultat", desc: "Renforce l'estime de soi et diminue les tensions au sein du groupe" },
        { term: "Prévention", desc: "Contribue à la prévention des comportements liés à la frustration" }
      ]
    },
    // Slide 19 - Comptines signées
    {
      type: 'content',
      title: "Les comptines signées",
      definition: "Les comptines signées constituent un support pédagogique très apprécié en crèche.",
      content: "Elles associent rythme, répétition, gestuelle et plaisir partagé. La répétition facilite la mémorisation des gestes et stimule l'attention. L'enfant observe, imite, expérimente.",
      extraInfo: [
        { term: "Moment collectif", desc: "Renforce le lien entre le professionnel et le groupe" },
        { term: "Inclusion", desc: "Favorise l'inclusion des enfants avec retard de langage, trouble de communication ou handicap auditif" }
      ]
    },
    // Slide 20 - Posture professionnelle signes
    {
      type: 'list',
      title: "Posture professionnelle dans l'utilisation des signes",
      items: [
        { term: "Cohérence", desc: "Utiliser les gestes avec régularité, toute l'équipe partage les mêmes signes" },
        { term: "Posture", desc: "Calme, bienveillante et encourageante" },
        { term: "Valorisation", desc: "Encourager chaque tentative de communication, même imparfaite" },
        { term: "Communication parents", desc: "Expliquer la démarche pour assurer la continuité maison/structure" },
        { term: "Rassurer", desc: "L'usage des signes ne retarde pas le langage oral mais le soutient" }
      ],
      alertColor: "#3B82F6"
    },
    // Slide 21 - Intérêt handicap signes
    {
      type: 'content',
      title: "Communication gestuelle et accompagnement du handicap",
      definition: "La communication gestuelle est particulièrement bénéfique pour les enfants présentant des troubles du langage, des troubles du spectre de l'autisme ou une déficience auditive.",
      content: "Elle constitue un outil d'inclusion, favorise la participation et réduit les situations d'isolement.",
      extraInfo: [
        { term: "Troubles du langage", desc: "Alternative d'expression en attendant le développement oral" },
        { term: "Troubles du spectre autistique", desc: "Support visuel rassurant et prévisible" },
        { term: "Déficience auditive", desc: "Lien naturel avec la LSF" }
      ]
    },
    // Slide 22 - Handicap
    {
      type: 'content',
      title: "Communication et accompagnement du handicap",
      definition: "L'accompagnement d'un enfant en situation de handicap nécessite une adaptation constante de la communication.",
      content: "L'auxiliaire de puériculture simplifie son langage, utilise des supports visuels et respecte le rythme de l'enfant. Elle favorise l'inclusion en permettant la participation aux activités collectives.",
      extraInfo: [
        { term: "Adaptation", desc: "Simplifier le langage, supports visuels, respecter le rythme" },
        { term: "Inclusion", desc: "Permettre la participation aux activités collectives" },
        { term: "Communication parents", desc: "Respectueuse, valorisante et factuelle" }
      ]
    },
    // Slide 17 - Posture handicap
    {
      type: 'list',
      title: "Accompagnement du handicap - Bonnes pratiques",
      items: [
        { term: "Adapter la communication", desc: "Langage simplifié, supports visuels" },
        { term: "Respecter le rythme", desc: "De l'enfant dans ses apprentissages" },
        { term: "Favoriser l'inclusion", desc: "Participation aux activités collectives" },
        { term: "Posture calme et stable", desc: "Sécurise l'enfant et évite la majoration des angoisses" },
        { term: "Projet individualisé", desc: "Cadre de la communication avec les parents" }
      ],
      alertColor: "#3B82F6"
    },
    // Slide 18 - Urgence
    {
      type: 'content',
      title: "Communication en situation d'urgence",
      definition: "En situation d'urgence, la communication doit rester claire et maîtrisée.",
      content: "L'auxiliaire de puériculture garde un ton calme et rassure l'enfant. La priorité est de rester calme et sécuriser l'enfant. Les informations transmises sont factuelles et précises.",
      warning: "Un ton de voix élevé ne rassure pas un enfant stressé.",
      extraInfo: [
        { term: "Priorité", desc: "Rester calme et sécuriser l'enfant" },
        { term: "Transmission", desc: "Des faits précis et observables, pas d'hypothèses" },
        { term: "Traçabilité", desc: "Écrite pour garantir la sécurité professionnelle" }
      ]
    },
    // Slide 19 - Après urgence
    {
      type: 'list',
      title: "Après une situation d'urgence",
      items: [
        { term: "Ne pas oublier l'incident", desc: "L'analyser pour progresser" },
        { term: "Transmission écrite et orale", desc: "Assurer la continuité et la traçabilité" },
        { term: "Ne pas modifier seul les protocoles", desc: "En discuter en équipe" },
        { term: "Gestion émotionnelle", desc: "Prendre du recul si nécessaire" }
      ],
      alertColor: "#EF4444"
    },
    // Slide 20 - Numéros d'urgence
    {
      type: 'infographic',
      title: "Urgence - Qui appeler ?",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/zovn5yuh_image.png"
    },
    // Slide 21 - Section Posture
    {
      type: 'section',
      title: "POSTURE PROFESSIONNELLE",
      subtitle: "Non verbal et analyse de pratique",
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)"
    },
    // Slide 21 - Posture non verbale
    {
      type: 'content',
      title: "La posture non verbale professionnelle",
      definition: "La posture non verbale est un outil de communication puissant.",
      content: "Un regard attentif, un sourire sincère, une voix posée et des gestes lents traduisent la disponibilité et la bienveillance. La maîtrise des émotions fait partie des compétences attendues.",
      extraInfo: [
        { term: "Posture ouverte", desc: "Favorise la confiance (vs bras croisés, regard fuyant)" },
        { term: "Distance professionnelle", desc: "Ajustée, alliant empathie et cadre" },
        { term: "Se mettre à hauteur", desc: "De l'enfant favorise la relation" }
      ]
    },
    // Slide 22 - Gestion émotions
    {
      type: 'list',
      title: "Gestion des émotions",
      items: [
        { term: "Communication professionnelle", desc: "Nécessite une gestion de ses émotions" },
        { term: "L'enfant perçoit", desc: "L'état interne de l'adulte" },
        { term: "Posture stable", desc: "Contribue à contenir l'angoisse de l'enfant" },
        { term: "Distance professionnelle", desc: "Maintenir empathie et cadre" }
      ],
      alertColor: "#10B981"
    },
    // Slide 23 - Désaccord parent
    {
      type: 'content',
      title: "En cas de désaccord avec un parent",
      definition: "Lors d'un désaccord avec un parent, il est conseillé d'écouter et reformuler.",
      content: "Ne pas imposer son point de vue, ne pas éviter la discussion, ne pas laisser la situation se régler seule. L'écoute active et la reformulation permettent de désamorcer les tensions.",
      extraInfo: [
        { term: "À faire", desc: "Écouter, reformuler, chercher un terrain d'entente" },
        { term: "À éviter", desc: "Imposer, fuir, ignorer le problème" }
      ]
    },
    // Slide 24 - Interculturel
    {
      type: 'content',
      title: "Communication interculturelle",
      definition: "La communication interculturelle demande d'adopter une posture ouverte et respectueuse.",
      content: "Il ne s'agit pas d'imposer les règles françaises ni de minimiser les différences. Le dialogue et le respect mutuel permettent de construire un partenariat de qualité.",
      extraInfo: [
        { term: "Posture", desc: "Ouverte et respectueuse des différences" },
        { term: "Objectif", desc: "Dialogue et respect mutuel" }
      ]
    },
    // Slide 25 - Analyse pratique
    {
      type: 'content',
      title: "Analyse de pratique et posture réflexive",
      definition: "La communication nécessite une remise en question régulière.",
      content: "L'analyse de situations vécues permet d'améliorer sa posture et d'ajuster ses pratiques. Se questionner sur sa manière d'accueillir, d'informer et de réagir face aux émotions contribue au développement professionnel."
    },
    // Slide 26 - Messages clés
    {
      type: 'keyMessage',
      title: "Messages clés à retenir",
      messages: [
        "La communication est un pilier du métier d'auxiliaire de puériculture",
        "Elle participe à la sécurité affective de l'enfant",
        "Elle renforce le partenariat avec les familles",
        "Elle prévient les risques",
        "Accueillir, informer et communiquer exigent des compétences relationnelles fines"
      ],
      gradient: "linear-gradient(135deg, #EC4899 0%, #DB2777 100%)"
    },
    // Slide 27 - Objectifs communication
    {
      type: 'list',
      title: "La communication professionnelle vise à :",
      items: [
        { term: "Sécuriser", desc: "L'enfant et sa famille" },
        { term: "Informer", desc: "De manière claire et professionnelle" },
        { term: "Créer un partenariat", desc: "Avec les familles et l'équipe" },
        { term: "Posture cohérente", desc: "Entre verbal et non verbal" },
        { term: "Adaptation", desc: "Aux situations ordinaires comme exceptionnelles" }
      ],
      alertColor: "#EC4899"
    },
    // Slide 28 - Félicitations
    {
      type: 'congratulations',
      title: "Félicitations !",
      message: "Vous avez terminé le cours sur l'Activité 6 : Accueil, information et communication.",
      nextStep: "Testez vos connaissances avec le quiz !",
      gradient: "linear-gradient(135deg, #EC4899 0%, #DB2777 50%, #BE185D 100%)"
    }
  ];

  // Quiz questions - 20 questions
  const quizQuestions = [
    {
      id: 1,
      question: "Quel est l'objectif principal de l'accueil du matin ?",
      options: [
        "Gagner du temps",
        "Sécuriser la séparation et recueillir les informations utiles",
        "Vérifier la tenue de l'enfant",
        "Organiser la journée de l'équipe"
      ],
      correct: 1
    },
    {
      id: 2,
      question: "La transmission du soir doit être brève pour ne pas retarder les parents :",
      options: [
        "Vrai",
        "Faux"
      ],
      correct: 1
    },
    {
      id: 3,
      question: "Lors d'une période de familiarisation, le rôle principal de l'AP est :",
      options: [
        "Observer uniquement l'enfant",
        "Remplacer le parent",
        "Accompagner progressivement la séparation",
        "Tester l'autonomie de l'enfant"
      ],
      correct: 2
    },
    {
      id: 4,
      question: "Quelle posture non verbale favorise la confiance ?",
      options: [
        "Bras croisés",
        "Regard fuyant",
        "Posture ouverte et sourire",
        "Distance excessive"
      ],
      correct: 2
    },
    {
      id: 5,
      question: "Il est possible de transmettre des informations sensibles devant d'autres familles si cela va plus vite :",
      options: [
        "Vrai",
        "Faux"
      ],
      correct: 1
    },
    {
      id: 6,
      question: "En communication avec l'enfant, verbaliser les actions permet :",
      options: [
        "De remplir le silence",
        "De structurer le langage et sécuriser",
        "De faire plaisir aux parents",
        "De gagner du temps"
      ],
      correct: 1
    },
    {
      id: 7,
      question: "En cas d'urgence, la priorité est :",
      options: [
        "Prévenir les parents immédiatement",
        "Rester calme et sécuriser l'enfant",
        "Remplir la fiche d'incident",
        "Appeler toute l'équipe"
      ],
      correct: 1
    },
    {
      id: 8,
      question: "Lors d'un appel aux services d'urgence, il faut transmettre :",
      options: [
        "Ses impressions personnelles",
        "Des hypothèses",
        "Des faits précis et observables",
        "L'avis des autres parents"
      ],
      correct: 2
    },
    {
      id: 9,
      question: "La communication avec un enfant en situation de handicap doit être identique à celle des autres enfants :",
      options: [
        "Vrai",
        "Faux - Elle doit être adaptée"
      ],
      correct: 1
    },
    {
      id: 10,
      question: "L'utilisation de pictogrammes en crèche permet :",
      options: [
        "De décorer la structure",
        "De remplacer totalement la parole",
        "De soutenir la compréhension",
        "De faire comme à l'école"
      ],
      correct: 2
    },
    {
      id: 11,
      question: "La communication professionnelle nécessite une gestion de ses émotions :",
      options: [
        "Vrai",
        "Faux"
      ],
      correct: 0
    },
    {
      id: 12,
      question: "Lors d'un désaccord avec un parent, il est conseillé :",
      options: [
        "D'imposer son point de vue",
        "D'éviter la discussion",
        "D'écouter et reformuler",
        "De laisser la situation se régler seule"
      ],
      correct: 2
    },
    {
      id: 13,
      question: "La transmission écrite permet :",
      options: [
        "D'éviter de parler",
        "D'assurer la traçabilité",
        "De remplacer l'équipe",
        "De rassurer uniquement les parents"
      ],
      correct: 1
    },
    {
      id: 14,
      question: "Se mettre à hauteur d'enfant favorise la relation :",
      options: [
        "Vrai",
        "Faux"
      ],
      correct: 0
    },
    {
      id: 15,
      question: "Dans l'accompagnement du handicap, il est important :",
      options: [
        "De surprotéger l'enfant",
        "D'adapter la communication",
        "D'éviter les activités collectives",
        "De parler uniquement aux parents"
      ],
      correct: 1
    },
    {
      id: 16,
      question: "La communication interculturelle demande :",
      options: [
        "D'imposer les règles françaises",
        "D'éviter le dialogue",
        "D'adopter une posture ouverte et respectueuse",
        "De minimiser les différences"
      ],
      correct: 2
    },
    {
      id: 17,
      question: "Un ton de voix élevé peut rassurer un enfant stressé :",
      options: [
        "Vrai",
        "Faux"
      ],
      correct: 1
    },
    {
      id: 18,
      question: "Le café parents permet principalement :",
      options: [
        "De contrôler les familles",
        "De renforcer le partenariat éducatif",
        "De présenter les règles disciplinaires",
        "De distribuer des documents administratifs"
      ],
      correct: 1
    },
    {
      id: 19,
      question: "En situation d'urgence, après l'événement il faut :",
      options: [
        "Oublier l'incident",
        "Éviter d'en parler",
        "Assurer une transmission écrite et orale",
        "Modifier seul les protocoles"
      ],
      correct: 2
    },
    {
      id: 20,
      question: "La communication professionnelle vise principalement :",
      options: [
        "À maintenir l'autorité",
        "À sécuriser, informer et créer un partenariat",
        "À éviter les conflits",
        "À appliquer des règles strictes"
      ],
      correct: 1
    }
  ];

  const getSlideText = (slide) => {
    let text = '';
    if (slide.title) text += slide.title + '. ';
    if (slide.subtitle) text += slide.subtitle + '. ';
    if (slide.definition) text += slide.definition + ' ';
    if (slide.content) text += slide.content + ' ';
    if (slide.warning) text += 'Attention : ' + slide.warning + ' ';
    if (slide.message) text += slide.message + ' ';
    if (slide.nextStep) text += slide.nextStep + ' ';
    
    if (slide.items) {
      slide.items.forEach(item => {
        text += item.term + ': ' + item.desc + '. ';
      });
    }
    if (slide.extraInfo) {
      slide.extraInfo.forEach(info => {
        text += info.term + ': ' + info.desc + '. ';
      });
    }
    if (slide.messages) {
      slide.messages.forEach(msg => {
        text += msg + '. ';
      });
    }
    if (slide.sections) {
      slide.sections.forEach(section => {
        text += section.title + ': ' + section.items.join(', ') + '. ';
      });
    }
    
    return text;
  };

  const handleListen = async () => {
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsPlaying(false);
      setIsLoading(false);
      return;
    }

    const slide = slides[currentSlide];
    let textToSpeak = getSlideText(slide);
    
    if (textToSpeak.length > 800) {
      textToSpeak = textToSpeak.substring(0, 800) + '...';
    }

    textToSpeak = textToSpeak
      .replace(/AP/g, 'auxiliaire de puériculture')
      .replace(/DEAP/g, 'diplôme d\'état d\'auxiliaire de puériculture');

    if (!textToSpeak || textToSpeak.trim() === '') {
      toast.info("Pas de contenu audio pour cette slide");
      return;
    }

    try {
      setIsLoading(true);
      setIsPlaying(true);
      const API_URL = window.location.origin || '';
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: textToSpeak, speed: 1.1 })
      });

      if (!response.ok) throw new Error('TTS failed');

      const data = await response.json();
      const audioData = data.audio_base64 || data.audio;
      
      setIsLoading(false);
      
      if (audioData) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(audioData), c => c.charCodeAt(0))],
          { type: 'audio/mp3' }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        audioRef.current = new Audio(audioUrl);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => {
          toast.error("Erreur de lecture audio");
          setIsPlaying(false);
        };
        audioRef.current.play().catch(err => {
          console.error('Audio play error:', err);
          toast.error("Impossible de lire l'audio");
          setIsPlaying(false);
        });
      } else {
        toast.error("Pas de données audio reçues");
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.error("Erreur lors de la génération audio");
      setIsPlaying(false);
      setIsLoading(false);
    }
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      setShowQuiz(true);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const handleQuizAnswer = (questionId, answerIndex) => {
    if (!quizSubmitted) {
      setQuizAnswers({ ...quizAnswers, [questionId]: answerIndex });
    }
  };

  const submitQuiz = () => {
    let score = 0;
    quizQuestions.forEach(q => {
      if (quizAnswers[q.id] === q.correct) score++;
    });
    setQuizScore(score);
    setQuizSubmitted(true);
  };

  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(0);
  };

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-title"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">{slide.title}</h1>
            <p className="text-xl md:text-2xl text-center max-w-3xl opacity-90">{slide.subtitle}</p>
          </div>
        );

      case 'sommaire':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white" data-testid="slide-sommaire">
            <h2 className="text-3xl font-bold mb-8 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {slide.sections.map((section, idx) => (
                <div 
                  key={idx}
                  onClick={() => section.slideTarget && goToSlide(section.slideTarget - 1)}
                  className="p-5 rounded-xl cursor-pointer transition-all hover:scale-105 hover:shadow-xl"
                  style={{ backgroundColor: section.color + '20', borderLeft: `4px solid ${section.color}` }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: section.color }}
                    >
                      {section.num}
                    </span>
                    <h3 className="text-xl font-semibold">{section.title}</h3>
                  </div>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span style={{ color: section.color }}>•</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'section':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-section"
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-4 text-center">{slide.title}</h2>
            {slide.subtitle && <p className="text-xl md:text-2xl opacity-90">{slide.subtitle}</p>}
          </div>
        );

      case 'content':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white overflow-y-auto" data-testid="slide-content">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-pink-400">{slide.title}</h2>
            <div className="space-y-4">
              {slide.definition && (
                <div className="p-4 bg-pink-500/20 rounded-xl border-l-4 border-pink-500">
                  <p className="text-lg font-medium">{slide.definition}</p>
                </div>
              )}
              {slide.content && <p className="text-gray-300 text-lg leading-relaxed">{slide.content}</p>}
              {slide.warning && (
                <div className="p-4 bg-red-500/20 rounded-xl border-l-4 border-red-500">
                  <p className="text-red-200 font-medium">{slide.warning}</p>
                </div>
              )}
              {slide.extraInfo && (
                <div className="space-y-3 mt-4">
                  {slide.extraInfo.map((info, idx) => (
                    <div key={idx} className="p-3 bg-slate-700/50 rounded-lg">
                      <span className="font-semibold text-pink-300">{info.term} : </span>
                      <span className="text-gray-300">{info.desc}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'contentLargeImage':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-6 rounded-2xl text-white overflow-y-auto" data-testid="slide-content-large">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-pink-400">{slide.title}</h2>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="lg:w-1/2 space-y-3">
                {slide.definition && (
                  <div className="p-3 bg-pink-500/20 rounded-xl border-l-4 border-pink-500">
                    <p className="text-base font-medium">{slide.definition}</p>
                  </div>
                )}
                {slide.content && <p className="text-gray-300 text-base leading-relaxed">{slide.content}</p>}
                {slide.extraInfo && (
                  <div className="space-y-2">
                    {slide.extraInfo.map((info, idx) => (
                      <div key={idx} className="p-2 bg-slate-700/50 rounded-lg">
                        <span className="font-semibold text-pink-300">{info.term} : </span>
                        <span className="text-gray-300 text-sm">{info.desc}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {slide.image && (
                <div className="lg:w-1/2 flex items-center justify-center">
                  <img src={slide.image} alt="" className="rounded-xl shadow-2xl max-h-[380px] w-auto object-contain" />
                </div>
              )}
            </div>
          </div>
        );

      case 'infographic':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-4 rounded-2xl text-white flex flex-col items-center justify-center" data-testid="slide-infographic">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-pink-400">{slide.title}</h2>
            <div className="flex-1 flex items-center justify-center w-full">
              <img 
                src={slide.image} 
                alt={slide.title} 
                className="max-h-[450px] w-auto rounded-xl shadow-2xl object-contain" 
              />
            </div>
          </div>
        );

      case 'list':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white overflow-y-auto" data-testid="slide-list">
            <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: slide.alertColor || '#EC4899' }}>{slide.title}</h2>
            <div className="space-y-4">
              {slide.items.map((item, idx) => (
                <div 
                  key={idx} 
                  className="p-4 rounded-xl transition-all hover:scale-[1.02]"
                  style={{ backgroundColor: (slide.alertColor || '#EC4899') + '15', borderLeft: `4px solid ${slide.alertColor || '#EC4899'}` }}
                >
                  <span className="font-bold text-lg" style={{ color: slide.alertColor || '#EC4899' }}>{item.term}</span>
                  <p className="text-gray-300 mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'keyMessage':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-key-message"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8">{slide.title}</h2>
            <div className="space-y-4 max-w-2xl">
              {slide.messages.map((msg, idx) => (
                <div key={idx} className="flex items-center gap-4 bg-white/10 p-4 rounded-xl backdrop-blur-sm">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-lg">{msg}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'congratulations':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-congratulations"
          >
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">{slide.title}</h2>
            <p className="text-xl text-center max-w-2xl mb-8 opacity-90">{slide.message}</p>
            <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
              <p className="text-lg font-medium">{slide.nextStep}</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (showQuiz) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-pink-900 to-slate-900 p-4 md:p-8" data-testid="quiz-container">
        <div className="max-w-4xl mx-auto">
          <div className="bg-slate-800/90 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-2xl">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
              Quiz - Activité 6 : Communication
            </h2>
            
            {quizSubmitted && (
              <div className={`mb-6 p-4 rounded-xl text-center ${quizScore >= 14 ? 'bg-green-500/20 border border-green-500' : 'bg-amber-500/20 border border-amber-500'}`}>
                <p className="text-2xl font-bold text-white">
                  Score : {quizScore} / {quizQuestions.length}
                </p>
                <p className={`${quizScore >= 14 ? 'text-green-300' : 'text-amber-300'}`}>
                  {quizScore >= 14 ? 'Excellent travail !' : 'Continuez à réviser !'}
                </p>
              </div>
            )}

            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
              {quizQuestions.map((q, qIdx) => (
                <div key={q.id} className="bg-slate-700/50 rounded-xl p-5">
                  <p className="text-white font-medium mb-4">
                    {qIdx + 1}. {q.question}
                  </p>
                  <div className="grid gap-2">
                    {q.options.map((option, oIdx) => {
                      let buttonClass = "w-full p-3 rounded-lg text-left transition-all ";
                      if (quizSubmitted) {
                        if (oIdx === q.correct) {
                          buttonClass += "bg-green-500/30 border-2 border-green-500 text-green-200";
                        } else if (quizAnswers[q.id] === oIdx && oIdx !== q.correct) {
                          buttonClass += "bg-red-500/30 border-2 border-red-500 text-red-200";
                        } else {
                          buttonClass += "bg-slate-600/50 text-gray-400";
                        }
                      } else {
                        buttonClass += quizAnswers[q.id] === oIdx 
                          ? "bg-pink-500/50 border-2 border-pink-500 text-white" 
                          : "bg-slate-600/50 hover:bg-slate-600 text-gray-300 border-2 border-transparent";
                      }
                      
                      return (
                        <button
                          key={oIdx}
                          onClick={() => handleQuizAnswer(q.id, oIdx)}
                          className={buttonClass}
                          disabled={quizSubmitted}
                          data-testid={`quiz-option-${q.id}-${oIdx}`}
                        >
                          <span className="flex items-center gap-3">
                            {quizSubmitted && oIdx === q.correct && <CheckCircle className="w-5 h-5 text-green-400" />}
                            {quizSubmitted && quizAnswers[q.id] === oIdx && oIdx !== q.correct && <XCircle className="w-5 h-5 text-red-400" />}
                            {option}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowQuiz(false)}
                className="flex-1 py-3 px-6 bg-slate-600 hover:bg-slate-500 text-white rounded-xl font-medium transition-all"
                data-testid="back-to-course-btn"
              >
                Retour au cours
              </button>
              {!quizSubmitted ? (
                <button
                  onClick={submitQuiz}
                  disabled={Object.keys(quizAnswers).length < quizQuestions.length}
                  className="flex-1 py-3 px-6 bg-pink-600 hover:bg-pink-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-all"
                  data-testid="submit-quiz-btn"
                >
                  Valider ({Object.keys(quizAnswers).length}/{quizQuestions.length})
                </button>
              ) : (
                <button
                  onClick={resetQuiz}
                  className="flex-1 py-3 px-6 bg-pink-600 hover:bg-pink-500 text-white rounded-xl font-medium transition-all"
                  data-testid="retry-quiz-btn"
                >
                  Recommencer
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-pink-900 to-slate-900 p-4 md:p-8" data-testid="presentation-container">
      <div className="max-w-6xl mx-auto">
        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Progression</span>
            <span>{currentSlide + 1} / {slides.length}</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-pink-500 to-rose-500 transition-all duration-300"
              style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Slide content */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden min-h-[500px]">
          {renderSlide(slides[currentSlide])}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-all"
            data-testid="prev-slide-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            Précédent
          </button>

          <button
            onClick={handleListen}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-all ${
              isPlaying 
                ? 'bg-red-600 hover:bg-red-500' 
                : 'bg-pink-600 hover:bg-pink-500'
            } text-white`}
            data-testid="listen-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Chargement...
              </>
            ) : isPlaying ? (
              <>
                <VolumeX className="w-5 h-5" />
                Arrêter
              </>
            ) : (
              <>
                <Volume2 className="w-5 h-5" />
                Écouter
              </>
            )}
          </button>

          <button
            onClick={nextSlide}
            className="flex items-center gap-2 px-6 py-3 bg-pink-600 hover:bg-pink-500 text-white rounded-xl transition-all"
            data-testid="next-slide-btn"
          >
            {currentSlide === slides.length - 1 ? 'Quiz' : 'Suivant'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DEAPActivite6Presentation;
